import { SellingMode, type ScheduleTime, type Scheduling, type TariffGroup } from '@src/core/buyerApiClient';
import { schedulingContainsMinute } from '@src/common/schedulings';
import { resolveDefaultTariffFromGroup } from '@src/domains/tariffs/core/resolveDefaultTariffFromGroup';
import { resolveSchedulingsForDate } from '@src/domains/scheduling/core/resolveSchedulingsForDate';
import type { PackageProductDates } from '../intersectPackageAvailableDates';
import { resolvePackageQuantityRange, type PackageQuantityBounds } from '../resolvePackageQuantityRange';

export type PackageProductSchedule = {
  dates?: PackageProductDates;
  defaultTariffGroup?: TariffGroup | null;
};

export type PackageProductForSingleBookingQuantity = PackageQuantityBounds & {
  productId: string;
  sellingMode?: SellingMode;
  schedule?: PackageProductSchedule | null;
};

export type ResolvePackageSingleBookingQuantityLimitsInput = {
  products: PackageProductForSingleBookingQuantity[];
  selectedDate?: string;
  selectedHour?: ScheduleTime;
  currentProductId?: string;
  currentProductTariffMax?: number;
};

type PackageProductLike = {
  productId: string;
  product?: {
    sellingMode?: SellingMode;
    minSaleTariffQuantity?: number | null;
    maxSaleTariffQuantity?: number | null;
  } | null;
};

export function mapPackageProductForSingleBookingQuantityLimits(
  item: PackageProductLike,
  sellingMode: SellingMode | undefined,
  schedule: PackageProductSchedule | null | undefined,
): PackageProductForSingleBookingQuantity {
  return {
    productId: item.productId,
    sellingMode,
    minSaleTariffQuantity: item.product?.minSaleTariffQuantity,
    maxSaleTariffQuantity: item.product?.maxSaleTariffQuantity,
    schedule,
  };
}

function resolveSchedulingForProduct(
  sellingMode: SellingMode | undefined,
  schedule: PackageProductSchedule | null | undefined,
  selectedDate: string | undefined,
  selectedMinutes: number | null,
): Scheduling | null {
  if (sellingMode === SellingMode.WithoutDate) {
    return null;
  }

  const availables = selectedDate ? resolveSchedulingsForDate(schedule?.dates, selectedDate)?.availables ?? [] : [];
  const availablesAtSelectedTime =
    sellingMode === SellingMode.WithDateAndTime && selectedMinutes != null
      ? availables.filter((scheduling) => schedulingContainsMinute(scheduling, selectedMinutes))
      : availables;

  return (
    availablesAtSelectedTime.find((scheduling) => resolveDefaultTariffFromGroup(scheduling.tariffGroup)) ??
    availablesAtSelectedTime[0] ??
    null
  );
}

function resolveRemainingCapacity(
  globalLimit: number | null | undefined,
  tariffLimit: number | null | undefined,
  productMax: number,
): number {
  const hasGlobalLimit = globalLimit !== null && globalLimit !== undefined;
  const hasTariffLimit = tariffLimit !== null && tariffLimit !== undefined;

  if (!hasGlobalLimit && !hasTariffLimit) {
    return productMax;
  }

  if (hasGlobalLimit && !hasTariffLimit) {
    return globalLimit === null ? productMax : Math.min(productMax, Math.max(0, globalLimit));
  }

  if (hasTariffLimit && !hasGlobalLimit) {
    return tariffLimit === null ? productMax : Math.min(productMax, Math.max(0, tariffLimit));
  }

  const globalRemaining = globalLimit == null ? productMax : Math.max(0, globalLimit);
  const tariffRemaining = tariffLimit == null ? productMax : Math.max(0, tariffLimit);

  return Math.min(productMax, globalRemaining, tariffRemaining);
}

function resolveOccupancyMaxFromScheduling(
  sellingMode: SellingMode | undefined,
  scheduling: Scheduling,
  tariffId: string,
  selectedMinutes: number | null,
  productMax: number,
): number {
  if (sellingMode === SellingMode.WithDateAndTime && selectedMinutes != null) {
    const general = scheduling.occupationRates.scheduleQuantities?.find((entry) => entry.minutes === selectedMinutes);
    const tariffSchedule = scheduling.occupationRates.tariffScheduleQuantities?.find(
      (entry) => entry.tariffId === tariffId,
    );
    const tariffOccupation = tariffSchedule?.occupationSchedule.find((entry) => entry.minutes === selectedMinutes);

    return resolveRemainingCapacity(
      general?.occupation.availableAmount,
      tariffOccupation?.occupation.availableAmount,
      productMax,
    );
  }

  const tariffQuantity = scheduling.occupationRates.tariffQuantities?.find((entry) => entry.tariffId === tariffId);

  return resolveRemainingCapacity(
    scheduling.occupationRates.quantity?.availableAmount,
    tariffQuantity?.occupation.availableAmount,
    productMax,
  );
}

function resolveOccupancyMaxForProduct(
  product: PackageProductForSingleBookingQuantity,
  selectedDate: string | undefined,
  selectedHour: ScheduleTime | undefined,
): number {
  const productMax = product.maxSaleTariffQuantity ?? 10;
  const sellingMode = product.sellingMode;
  const selectedMinutes = sellingMode === SellingMode.WithDateAndTime ? selectedHour?.minutes ?? null : null;

  if (sellingMode !== SellingMode.WithoutDate && !selectedDate) {
    return productMax;
  }

  if (sellingMode === SellingMode.WithoutDate) {
    const tariff = resolveDefaultTariffFromGroup(product.schedule?.defaultTariffGroup);
    return tariff ? productMax : 0;
  }

  const scheduling = resolveSchedulingForProduct(sellingMode, product.schedule, selectedDate, selectedMinutes);
  if (!scheduling) {
    return 0;
  }

  const tariff = resolveDefaultTariffFromGroup(scheduling.tariffGroup);
  if (!tariff) {
    return 0;
  }

  return resolveOccupancyMaxFromScheduling(sellingMode, scheduling, tariff.id, selectedMinutes, productMax);
}

export function resolvePackageSingleBookingQuantityLimits({
  products,
  selectedDate,
  selectedHour,
  currentProductId,
  currentProductTariffMax,
}: ResolvePackageSingleBookingQuantityLimitsInput): { min: number; max: number } {
  const packageRange = resolvePackageQuantityRange(products);

  const occupancyMaxes = products.map((product) => {
    if (product.productId === currentProductId && currentProductTariffMax != null) {
      return currentProductTariffMax;
    }
    return resolveOccupancyMaxForProduct(product, selectedDate, selectedHour);
  });

  const max = Math.min(packageRange.max, ...occupancyMaxes);

  return { min: packageRange.min, max };
}
